/**
 * A FIRST NAME OUT OF WHATEVER THE PHONE SYSTEM CALLED THEM.
 *
 * CallRail hands names back as "ZARAZUA,MARIA" -- surname first, no space
 * -- so splitting on whitespace returns the whole thing and the opener
 * went out reading "Hi ZARAZUA,MARIA". It also shouts: "DAYVON KENNEDY" in
 * capitals becomes "Hi DAYVON".
 *
 * And plenty of these are not names at all. CallRail fills the field with
 * a city when it has no caller id -- "LONG BEACH CA", "WIRELESS CALLER" --
 * and greeting somebody as a place is worse than not greeting them.
 *
 * Moved here from lead-textback.ts on 2026-10-06 (T0026), because only the
 * openers used it. The quote send, the quote chase and the booking text
 * each split the name on a space themselves, and in the thirty days to 6
 * October customers were greeted "Hi WIRELESS" 116 times, on quotes,
 * chases and booking confirmations. lead-textback.ts re-exports this, so
 * its callers did not move.
 *
 * One change from the old copy: a name typed in mixed case keeps its case.
 * "McKenzie" stays McKenzie. Only a name in all capitals or all lower case
 * is put into Title case, because that is the phone system shouting, not
 * the person spelling.
 */
export function firstNameOf(raw: string | null | undefined): string {
  const name = String(raw ?? "").trim();
  if (!name) return "";

  // Not a person: a place, a carrier's placeholder, or our own stand-in.
  if (/wireless|caller|unknown|private|toll free|unassigned/i.test(name)) return "";
  // A place, not a person: CallRail fills the field with the town and the
  // state when there is no caller id -- LONG BEACH CA, NORTH RICHLA TX.
  // Any run of capitals ending in a two-letter state code is one of those.
  if (/^[A-Z][A-Z\s]*\s+[A-Z]{2}$/.test(name)) return "";

  // "SURNAME,FIRST" is the phone system's shape; a comma means the first
  // name is on the right of it.
  const first = (name.includes(",")
    ? (name.split(",")[1] ?? "").trim() || (name.split(",")[0] ?? "").trim()
    : (name.split(/\s+/)[0] ?? "")).replace(/[.:;]+$/, "");

  if (first.length < 2 || /\d/.test(first)) return "";
  const shouted = first === first.toUpperCase() || first === first.toLowerCase();
  return shouted ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : first;
}
